import React, { FC, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { AlertColor, Box, CircularProgress, Container, Divider, Grid, Typography, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import Form, { FormDataModel } from '../../../components/Form';
import { removeErrorFieldsFromValues } from '../../../utils/validators';
import Notification from '../../../components/Notification';
import axiosInstance from '../../../utils/axios';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import Navigation from '../../../utils/hooks/Navigation';
import strings from 'common/Translation/Translate';
import { fetchErrorFields, ScrollTop } from 'utils/validators/HelperFunctions';
import { identifiers } from 'utils/constants/identifiers';
import { fetchFromStorage } from 'utils/storage';
import PageLoader from 'components/PageLoader';
import { pageTitleSx } from 'utils/styles/pageHeader';

export interface AddEditProps {
  pageConfig: any;
  pageResponse: object;
  handlePropsChange?: (key: string, value: any, formdata: any, deleted: any, formRef?: any, setHasError?: any) => void;
  handlePropsFocus?: (key: string, value: any, formdata: any, deleted: any) => void;
  handleCustomResponse?: (body: FormDataModel, preview?: string) => any;
  customCallbackResponse?: any;
  customButtonClick?: any;
  handleSecondaryButton?: any;
  isFormUpdated?: boolean;
  dropdownValue?: any;
  isFormData?: any;
  children?: any;
  customButton?: string;
  handleCustomButton?: (body: FormDataModel) => void;
  customHandleSave?: (body: FormDataModel, preview?: string) => void;
  ExtraButtons?: any;
  pageLoading?: boolean;
  externalFormRef?: RefObject<Form | null | undefined>;
  beforeForm?: React.ReactNode;
  headerToggle?: React.ReactNode;
}

const AddEdit: FC<AddEditProps> = ({
  pageConfig,
  pageResponse,
  handlePropsChange,
  handleCustomResponse,
  customCallbackResponse,
  customButtonClick,
  dropdownValue,
  handleSecondaryButton,
  handlePropsFocus,
  children,
  customButton,
  handleCustomButton,
  customHandleSave,
  ExtraButtons,
  pageLoading,
  externalFormRef,
  beforeForm,
  headerToggle,
}) => {
  const Navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams() as any;
  const internalFormRef: RefObject<Form | null | undefined> = useRef();
  const formRef = externalFormRef || internalFormRef;
  /** Sync guard so a second Save click in the same tick cannot start another POST/PUT. */
  const savingRef = useRef(false);
  const org = fetchFromStorage(identifiers.organization) as any;
  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(pageConfig.hasError);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  function handleCatch(error: any) {
    ScrollTop(0);
    setLoading(false);

    if (error.response) {
      if (error.response.status === 400) {
        const errors = error.response.data.errors;

        let errMessage;
        if (errors) {
          errMessage = errors.map((err: any) => `${err.field}: ${err.defaultMessage}`).join(', ');
        } else {
          errMessage = error.response.data.message;
        }

        setHasError(true);
        setMessage({
          display: true,
          severity: 'error',
          message: errMessage ?? 'There was an error processing your request.',
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description ?? error.response.data.message,
        });
      }
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  }

  useEffect(() => {
    if (customButtonClick > 0) {
      handleAddEdit('preview');
    }
  }, [customButtonClick]);

  function onSuccess(data: any) {
    setLoading(false);
    setMessage({
      display: true,
      severity: 'success',
      message: pageConfig.successMessageWithLink ? pageConfig.successMessage(data) : pageConfig.successMessage,
    });
    if (pageConfig.initialOrgNull) {
      if (customCallbackResponse) {
        customCallbackResponse(data);
      }
    } else {
      setTimeout(() => {
        if (customCallbackResponse) {
          customCallbackResponse(data);
        }
        if (pageConfig.navigateToPage && !pageConfig.skipSuccessRedirect) {
          Navigate(`${pageConfig.navigateToPage}`);
        }
      }, pageConfig.delayAfterSave || 1000);
    }
  }

  const handleAddEdit = async (e?: MouseEvent<HTMLButtonElement> | string) => {
    if (savingRef.current || loading) return;
    savingRef.current = true;
    try {
      const { getFormData } = formRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };
      const { formData, isFormValid } = getFormData();
      const body = removeErrorFieldsFromValues(formData);
      const method = id || pageConfig?.queryStingId ? 'put' : 'post';
      const customMethodHelper = pageConfig.customMethod?.toLowerCase() === 'put' ? 'put' : 'post';
      const customMethod = pageConfig.customMethod ? customMethodHelper : method;
      pageConfig.scrollAfterSave === undefined && ScrollTop(0);
      setHasError(false);
      if (isFormValid) {
        if (customHandleSave) {
          customHandleSave(body, e as any);
        } else {
          const requestData = handleCustomResponse ? handleCustomResponse(body, e as any) : body;
          if (requestData === (false as any)) {
            return false;
          }
          setLoading(true);
          const { status, data } = await axiosInstance[customMethod](
            pageConfig.addEditApiUrl,
            requestData,
            pageConfig.isFormData && {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );
          if (status === 200 || status === 201) {
            onSuccess(data);
          }
        }
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: `${strings.required_error_prefix_message} ${fetchErrorFields(formData, pageConfig.formModel).join(
            ', ',
          )}`,
        });
        setHasError(true);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      savingRef.current = false;
    }
  };

  const onHandleCustomButton = () => {
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const body = removeErrorFieldsFromValues(formData);
    pageConfig.scrollAfterSave === undefined && ScrollTop(0);
    setHasError(false);
    if (isFormValid) {
      handleCustomButton && handleCustomButton(body);
    } else {
      ScrollTop(0);
      setMessage({
        display: true,
        severity: 'error',
        message: `${strings.required_error_prefix_message} ${fetchErrorFields(formData, pageConfig.formModel).join(
          ', ',
        )}`,
      });
      setHasError(true);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleChange = (key: string, value: any, formdata: any, delted: any) => {
    if (handlePropsChange) {
      handlePropsChange(key, value, formdata, delted, formRef, setHasError);
    }
  };

  const handleFocus = (key: string, value: any, formdata: any, delted: any) => {
    if (handlePropsFocus) {
      handlePropsFocus(key, value, formdata, delted);
    }
  };

  const getButtonText = () => {
    if (pageConfig.primaryButtonLabel) return pageConfig.primaryButtonLabel;
    else if (id) return strings.common_button_save;
    else return strings.common_column_add;
  };
  return (
    <Container className="content p-0" maxWidth={false}>
      <Grid container spacing={2}>
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
          />
        )}
        {pageLoading && <PageLoader />}
        {!pageConfig.hideClose && (
          <>
            <Grid item xs={10}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={pageTitleSx}> {pageConfig.title}</Typography>
                {headerToggle && headerToggle}
              </Box>
            </Grid>
            {org !== '*' && (
              <Grid item textAlign="right" xs={2}>
                <Navigation url={pageConfig.navigateToPage || -1} />
              </Grid>
            )}
          </>
        )}
      </Grid>
      {!pageConfig.hideClose && <Divider sx={{ mt: 1, mb: 2 }} />}
      {beforeForm}
      {!pageConfig.hideForm && (
        <Grid container spacing={2} sx={{ my: 1 }} alignItems="flex-start">
          <Form
            key={pageConfig.formRefreshKey}
            isFormUpdated={id}
            dropdownData={dropdownValue ? dropdownValue : {}}
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={pageConfig.formModel}
            values={pageResponse}
            onChange={handleChange}
            onFocus={handleFocus}
          />
        </Grid>
      )}
      {children}
      <Grid container sx={{ mt: 1, display: 'flex', alignItems: 'center' }} gap={1}>
        {/* <Grid item xs={12} sm={3} md={2} lg={1}> */}
        {ExtraButtons && <ExtraButtons />}
        {!pageConfig?.saveButtonHide && (
          <PrimaryButton
            sx={{ minWidth: '100px', width: 'max-content', ml: ExtraButtons ? 'auto' : 0 }}
            disabled={loading}
            variant="contained"
            onClick={handleAddEdit}>
            {loading && <CircularProgress color="inherit" sx={{ mr: 1 }} size={20} />}
            {getButtonText()}
          </PrimaryButton>
        )}
        {pageConfig.secondaryButtonText && id && (
          <PrimaryButton
            disabled={!id}
            sx={{
              '&.defaultButton': { background: theme.palette.primary.main, color: theme.palette.primary.contrastText },
              width: 'max-content',
            }}
            variant="contained"
            onClick={() => handleSecondaryButton(id)}>
            {loading && <CircularProgress color="inherit" sx={{ ml: 1 }} size={20} />}
            {pageConfig.secondaryButtonText}
          </PrimaryButton>
        )}
        {customButton && (
          <PrimaryButton
            sx={{
              '&.defaultButton': { background: theme.palette.primary.main, color: theme.palette.primary.contrastText },
              width: 'max-content',
            }}
            variant="contained"
            onClick={() => onHandleCustomButton()}
            disabled={loading}>
            {customButton}
          </PrimaryButton>
        )}
      </Grid>
    </Container>
  );
};
export default AddEdit;
